import type { ReactNode } from 'react';
import { Check, LoaderCircle, Minus } from 'lucide-react';
import { cn } from '@design-system';

export type ProgressStepStatus = 'completed' | 'current' | 'upcoming' | 'skipped';

interface ProgressStepProps {
  name: string;
  status: ProgressStepStatus;
  // How far through the step the user is; shown on the current (or partly completed) step
  percentComplete?: number;
  badge?: ReactNode;
}

/** One step of the plan progress bar: a bar segment with the step's name under it. */
export function ProgressStep({ name, status, percentComplete = 100, badge }: ProgressStepProps) {
  const showFill = status === 'current' || (status === 'completed' && percentComplete < 100);

  return (
    <>
      <div
        className={cn('mb-2 h-1 w-full rounded-full transition-colors duration-300', {
          'bg-primary/30': status === 'current',
          'bg-accent': status === 'upcoming',
          'bg-primary': status === 'completed',
          'bg-muted': status === 'skipped',
        })}
      >
        {showFill && (
          <div
            className="bg-primary h-1 rounded-full"
            style={{ width: `${status === 'completed' ? 100 : percentComplete}%` }}
          />
        )}
      </div>

      <div
        className={cn('text-foreground flex items-center gap-2 text-xs font-medium md:text-sm', {
          'text-primary font-semibold': status === 'current',
        })}
      >
        {status === 'completed' && (
          <div className="grid size-5 place-items-center rounded-full border-green-600/20 bg-green-600/5 text-green-800">
            <Check className="size-4" />
          </div>
        )}
        {status === 'skipped' && (
          <div className="bg-accent grid size-5 place-items-center rounded-full">
            <Minus className="size-4" />
          </div>
        )}
        {status === 'current' && <LoaderCircle className="text-primary hidden size-4 self-center md:flex" />}
        <span>{name}</span>
        {badge}
      </div>
    </>
  );
}
